// validators/phoneE164.ts
import { trimWs } from '@/@core/utils/string-utils';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export type ValidateE164PhoneOptions = {
    allowedCountries?: string[];
};

export type E164PhoneValidationErrorCode =
    | 'TYPE'
    | 'EMPTY'
    | 'PLUS'
    | 'LENGTH'
    | 'PARSE'
    | 'INVALID'
    | 'COUNTRY'
    | 'NOT_NORMALIZED';

export type E164PhoneValidationResult =
    | { ok: true; value: string; country?: string }
    | { ok: false; code: E164PhoneValidationErrorCode; message: string };

const ERROR_MESSAGES_SK: Record<E164PhoneValidationErrorCode, string> = {
    TYPE: 'Telefónne číslo musí byť textový reťazec',
    EMPTY: 'Telefónne číslo je povinné',
    PLUS: 'Telefónne číslo musí začínať znakom +',
    LENGTH: 'Telefónne číslo nie je v platnom formáte (napr. +421 905 123 456)',
    PARSE: 'Telefónne číslo nie je v platnom formáte (napr. +421 905 123 456)',
    INVALID: 'Telefónne číslo nie je v platnom formáte',
    COUNTRY: 'Telefónne číslo nie je z povolenej krajiny',
    NOT_NORMALIZED: 'Telefónne číslo musí byť v normalizovanom formáte (napr. +421 905 123 456).',
};

export function validateMobilePhoneE164(
    value: unknown,
    opts: ValidateE164PhoneOptions = {},
): E164PhoneValidationResult {
    const { allowedCountries = ['SK', 'CZ'] } = opts;
    const minLength = 9;
    const maxLength = 25;

    if (typeof value !== 'string') {
        return { ok: false, code: 'TYPE', message: ERROR_MESSAGES_SK.TYPE };
    }

    const raw = trimWs(value);

    if (!raw) {
        return { ok: false, code: 'EMPTY', message: ERROR_MESSAGES_SK.EMPTY };
    }

    if (!raw.startsWith('+')) {
        return { ok: false, code: 'PLUS', message: ERROR_MESSAGES_SK.PLUS };
    }

    if (raw.length < minLength || raw.length > maxLength) {
        return {
            ok: false,
            code: 'LENGTH',
            message: `${ERROR_MESSAGES_SK.LENGTH} Povolený rozsah je ${minLength}–${maxLength} znakov.`,
        };
    }

    const parsed = parsePhoneNumberFromString(raw);
    if (!parsed) {
        return { ok: false, code: 'PARSE', message: ERROR_MESSAGES_SK.PARSE };
    }

    if (!parsed.isValid()) {
        return { ok: false, code: 'INVALID', message: ERROR_MESSAGES_SK.INVALID };
    }

    if (allowedCountries?.length) {
        if (!parsed.country || !allowedCountries.includes(parsed.country)) {
            return {
                ok: false,
                code: 'COUNTRY',
                message: `${ERROR_MESSAGES_SK.COUNTRY}. Povolené krajiny: ${allowedCountries.join(
                    ', ',
                )}.`,
            };
        }
    }

    if (parsed.number !== raw) {
        return {
            ok: false,
            code: 'NOT_NORMALIZED',
            message: ERROR_MESSAGES_SK.NOT_NORMALIZED,
        };
    }

    return {
        ok: true,
        value: raw,
        country: parsed.country ?? undefined,
    };
}
